'use client'

import React, { useCallback, useEffect, useId, useRef, useState } from 'react'
import { ArrowLeft, Copy, QrCode, Share2 } from 'lucide-react'
import { WagamourV2Modal } from '@/components/v2/WagamourV2Modal'
import { WagamourV2Button } from '@/components/v2/WagamourV2Button'
import { WagamourV2ModalEyebrow } from '@/components/v2/WagamourV2ModalContent'
import { PlayCircleOrbitAvatar } from '@/components/play/PlayCircleOrbitAvatar'
import {
  ConnectDogCardQrCode,
  ConnectDogCardQrInstruction,
} from '@/components/play/ConnectDogCardQrCode'
import {
  buildConnectDogCardSharePayload,
  copyConnectDogCardUrl,
  formatConnectShareExpiry,
  isWebShareSupported,
  shareConnectDogCard,
} from '@/lib/play/connectShareAdapter'
import { useConnectShareTokenSession } from '@/lib/play/useConnectShareTokenSession'
import type { ConnectShareTokenCreateResult } from '@/lib/play/useConnectShareTokenSession'
import { isConnectDogCardQrUrl } from '@/lib/play/playQrContracts'
import { PLAY_CONNECT_DOG_CARD_PREVIEW_WINDOW_NAME } from '@/lib/play/playConnectConstants'
import { v2ModalStyles } from '@/lib/design/v2ModalContent'
import { homeV2Text, homeV2Type } from '@/lib/home/homeV2Theme'
import { playV2FocusRing } from '@/lib/play/playV2Focus'
import { trackPlayV2Event } from '@/lib/play/playV2Analytics'

export type ConnectOurDogsShareDog = {
  name: string
  photoUrl: string | null
  breed?: string | null
}

export type ConnectOurDogsShareSheetProps = {
  isOpen: boolean
  onClose: () => void
  dog: ConnectOurDogsShareDog
  createShareToken: () => Promise<ConnectShareTokenCreateResult>
  /** Dev-only: explicit recipient preview control */
  showPreviewRecipientAction?: boolean
  /** Portal target for canvas-anchored sheet (integrated preview). */
  portalContainer?: HTMLElement | null
  returnFocusRef?: React.RefObject<HTMLElement | null>
  userErrorMessage?: string
  /** Dev-only QR contract diagnostics (no raw token). */
  showQrDiagnostics?: boolean
}

type SheetView = 'share' | 'qr'

const SHEET_USER_ERROR = "We couldn't prepare Milo's Dog Card. Please try again."

export function ConnectOurDogsShareSheet({
  isOpen,
  onClose,
  dog,
  createShareToken,
  showPreviewRecipientAction = false,
  portalContainer = null,
  returnFocusRef,
  userErrorMessage = SHEET_USER_ERROR,
  showQrDiagnostics = false,
}: ConnectOurDogsShareSheetProps) {
  const titleId = useId()
  const statusId = useId()
  const [view, setView] = useState<SheetView>('share')
  const [actionBusy, setActionBusy] = useState(false)
  const [statusMessage, setStatusMessage] = useState<string | null>(null)
  const [manualCopyUrl, setManualCopyUrl] = useState<string | null>(null)
  const [previewFallbackUrl, setPreviewFallbackUrl] = useState<string | null>(null)
  const openedRef = useRef(false)

  const { state, ensureToken, reset } = useConnectShareTokenSession({
    createShareToken,
    userErrorMessage,
  })

  const resetSheet = useCallback(() => {
    setView('share')
    setActionBusy(false)
    setStatusMessage(null)
    setManualCopyUrl(null)
    setPreviewFallbackUrl(null)
    reset()
  }, [reset])

  const handleClose = useCallback(() => {
    resetSheet()
    onClose()
    window.requestAnimationFrame(() => {
      returnFocusRef?.current?.focus()
    })
  }, [onClose, resetSheet, returnFocusRef])

  useEffect(() => {
    if (isOpen) {
      openedRef.current = true
      return
    }
    if (openedRef.current) {
      resetSheet()
    }
  }, [isOpen, resetSheet])

  const runWithToken = useCallback(
    async (
      action: (dogCardUrl: string, expiresAt: string | null) => Promise<void>
    ) => {
      if (actionBusy) return
      setActionBusy(true)
      setStatusMessage(null)
      setManualCopyUrl(null)
      try {
        const token = await ensureToken()
        if (!token.ok) {
          setStatusMessage(token.message)
          return
        }
        await action(token.dogCardUrl, token.expiresAt)
      } finally {
        setActionBusy(false)
      }
    },
    [actionBusy, ensureToken]
  )

  const handleShareDogCard = () => {
    void runWithToken(async (dogCardUrl) => {
      if (!isWebShareSupported()) {
        const copied = await copyConnectDogCardUrl(dogCardUrl)
        if (copied.outcome === 'copied') {
          setStatusMessage('Dog Card link copied.')
        } else if (copied.outcome === 'manual_required') {
          setManualCopyUrl(copied.url)
          setStatusMessage('Copy the link below.')
        } else {
          setStatusMessage("We couldn't share the Dog Card. Try Copy Link.")
        }
        return
      }

      const payload = buildConnectDogCardSharePayload(dog.name, dogCardUrl)
      const result = await shareConnectDogCard(payload)
      if (result.outcome === 'shared') {
        setStatusMessage('Dog Card shared.')
        trackPlayV2Event('connect_shared', {
          source: 'connect',
          result: 'shared',
        })
      } else if (result.outcome === 'cancelled') {
        setStatusMessage(null)
      } else if (result.outcome === 'unavailable') {
        const copied = await copyConnectDogCardUrl(dogCardUrl)
        if (copied.outcome === 'copied') {
          setStatusMessage('Dog Card link copied.')
          trackPlayV2Event('connect_shared', {
            source: 'connect',
            result: 'copied',
          })
        } else if (copied.outcome === 'manual_required') {
          setManualCopyUrl(copied.url)
          setStatusMessage('Copy the link below.')
        } else {
          setStatusMessage("We couldn't share the Dog Card. Try Copy Link.")
        }
      } else {
        setStatusMessage(result.message)
      }
    })
  }

  const handleCopyLink = () => {
    void runWithToken(async (dogCardUrl) => {
      const result = await copyConnectDogCardUrl(dogCardUrl)
      if (result.outcome === 'copied') {
        setStatusMessage('Dog Card link copied.')
        setManualCopyUrl(null)
        trackPlayV2Event('connect_shared', {
          source: 'connect',
          result: 'copied',
        })
      } else if (result.outcome === 'manual_required') {
        setManualCopyUrl(result.url)
        setStatusMessage('Copy the link below.')
      } else {
        setStatusMessage("We couldn't copy the link. Please try again.")
      }
    })
  }

  const handleShowQr = () => {
    void runWithToken(async () => {
      setView('qr')
      setStatusMessage(null)
      setManualCopyUrl(null)
    })
  }

  const handlePreviewRecipient = () => {
    void runWithToken(async (dogCardUrl) => {
      const popup = window.open(dogCardUrl, PLAY_CONNECT_DOG_CARD_PREVIEW_WINDOW_NAME, 'noopener,noreferrer')
      if (!popup) {
        setPreviewFallbackUrl(dogCardUrl)
        setStatusMessage('Popup blocked — use the link below to preview.')
        return
      }
      setPreviewFallbackUrl(null)
      setStatusMessage('Recipient preview opened in a new tab.')
    })
  }

  const expiryLabel = formatConnectShareExpiry(state.expiresAt)
  const dogName = dog.name.trim() || 'your dog'
  const shareBusy = actionBusy || state.status === 'loading'

  return (
    <WagamourV2Modal
      isOpen={isOpen}
      onClose={handleClose}
      presentation="sheet"
      portalContainer={portalContainer}
      preventClose={shareBusy}
      ariaLabelledBy={titleId}
      header={
        <div className="space-y-1">
          {view === 'qr' ? (
            <button
              type="button"
              className={`mb-1 inline-flex min-h-[44px] items-center gap-1 text-sm text-white/75 hover:text-white ${playV2FocusRing}`}
              onClick={() => {
                setView('share')
                setStatusMessage(null)
              }}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden />
              Back
            </button>
          ) : null}
          <WagamourV2ModalEyebrow>Connect</WagamourV2ModalEyebrow>
          <h2 id={titleId} className="text-base font-bold leading-snug" style={v2ModalStyles.headline}>
            {view === 'qr' ? `Scan ${dogName}'s Dog Card` : 'Connect our dogs'}
          </h2>
        </div>
      }
      footer={
        view === 'share' ? (
          <div className="space-y-2">
            <WagamourV2Button
              type="button"
              fullWidth
              disabled={shareBusy}
              onClick={handleShareDogCard}
              data-connect-share-primary="true"
            >
              <span className="inline-flex items-center justify-center gap-2">
                <Share2 className="h-4 w-4" aria-hidden />
                {shareBusy ? 'Preparing…' : 'Share Dog Card'}
              </span>
            </WagamourV2Button>
            <div className="grid grid-cols-2 gap-2">
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={shareBusy}
                onClick={handleShowQr}
                data-connect-share-qr="true"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <QrCode className="h-4 w-4" aria-hidden />
                  Show QR Code
                </span>
              </WagamourV2Button>
              <WagamourV2Button
                type="button"
                fullWidth
                variant="secondary"
                disabled={shareBusy}
                onClick={handleCopyLink}
                data-connect-share-copy="true"
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <Copy className="h-4 w-4" aria-hidden />
                  Copy Link
                </span>
              </WagamourV2Button>
            </div>
            {showPreviewRecipientAction ? (
              <button
                type="button"
                className={`mx-auto block min-h-[44px] w-full rounded-full px-3 text-center text-[11px] font-medium text-amber-200/90 underline [text-underline-offset:2px] ${playV2FocusRing}`}
                disabled={shareBusy}
                onClick={handlePreviewRecipient}
                data-connect-share-preview-recipient="true"
              >
                Preview recipient view (dev only)
              </button>
            ) : null}
          </div>
        ) : undefined
      }
      contentClassName="pb-[max(0.5rem,env(safe-area-inset-bottom,0px))]"
    >
      <div
        className="space-y-4"
        aria-labelledby={titleId}
        data-connect-our-dogs-share-sheet="true"
        data-connect-share-view={view}
      >
        {view === 'share' ? (
          <>
            <div className="flex items-center gap-3 rounded-[20px] border border-white/10 bg-white/5 p-3">
              <PlayCircleOrbitAvatar
                name={dog.name}
                photoUrl={dog.photoUrl}
                size={56}
                variant="center"
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold" style={homeV2Type(homeV2Text.headline)}>
                  {dog.name}
                </p>
                {dog.breed ? (
                  <p className="text-xs text-white/65">{dog.breed}</p>
                ) : null}
              </div>
            </div>

            <p className="text-sm leading-relaxed" style={v2ModalStyles.body}>
              Share {dogName}&apos;s Dog Card with a dog parent you&apos;ve already met.
            </p>
            <p className="text-xs leading-relaxed text-white/65">
              Only {dogName}&apos;s public play details are shared.
            </p>

            {state.status === 'error' && state.errorMessage ? (
              <p className="rounded-xl border border-amber-300/25 bg-amber-500/10 px-3 py-2 text-sm text-amber-100" role="alert">
                {state.errorMessage}
              </p>
            ) : null}
          </>
        ) : (
          <div
            className="space-y-4 py-2"
            data-connect-qr-panel="true"
            data-connect-qr-encoded-url={state.dogCardUrl ?? undefined}
          >
            <ConnectDogCardQrInstruction id={`${titleId}-qr-instruction`} />
            {state.dogCardUrl ? (
              <ConnectDogCardQrCode url={state.dogCardUrl} size={220} />
            ) : (
              <div className="flex justify-center py-8">
                <p className="text-sm text-white/70">Preparing QR code…</p>
              </div>
            )}
            {expiryLabel ? (
              <p className="text-center text-xs text-white/60">
                This link stays active until {expiryLabel}.
              </p>
            ) : null}
            {showQrDiagnostics && state.dogCardUrl && isConnectDogCardQrUrl(state.dogCardUrl) ? (
              <dl
                className="rounded-xl border border-white/10 bg-black/25 px-3 py-2 text-[11px] text-white/70"
                data-connect-qr-diagnostics="true"
              >
                <div className="flex justify-between gap-2">
                  <dt>Dog-specific QR</dt>
                  <dd>true</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt>Token-backed</dt>
                  <dd>true</dd>
                </div>
                {expiryLabel ? (
                  <div className="flex justify-between gap-2">
                    <dt>Expires</dt>
                    <dd>{expiryLabel}</dd>
                  </div>
                ) : null}
              </dl>
            ) : null}
          </div>
        )}

        <p
          id={statusId}
          className="min-h-[1.25rem] text-center text-xs text-emerald-200/90"
          role="status"
          aria-live="polite"
          data-connect-share-status="true"
        >
          {statusMessage}
        </p>

        {manualCopyUrl ? (
          <div className="rounded-xl border border-white/15 bg-black/30 p-3">
            <p className="mb-2 text-xs text-white/70">Select and copy this link:</p>
            <input
              type="text"
              readOnly
              value={manualCopyUrl}
              className="w-full rounded-lg border border-white/10 bg-black/40 px-2 py-2 text-xs text-white"
              onFocus={(e) => e.currentTarget.select()}
              aria-label="Dog Card link for manual copy"
              data-connect-share-manual-copy="true"
            />
          </div>
        ) : null}

        {previewFallbackUrl ? (
          <a
            href={previewFallbackUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`block text-center text-xs font-medium text-amber-100 underline [text-underline-offset:2px] ${playV2FocusRing}`}
            data-connect-share-preview-fallback="true"
          >
            Open recipient preview
          </a>
        ) : null}
      </div>
    </WagamourV2Modal>
  )
}
